// components/Common/NumberTextField.tsx
import React from 'react';
import { TextField, TextFieldProps } from '@mui/material';

interface NumberTextFieldProps extends Omit<TextFieldProps, 'onChange'> {
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  thousandSeparator?: boolean; // カンマ区切り表示の有無を指定するオプション
}

export const NumberTextField = React.forwardRef<HTMLDivElement, NumberTextFieldProps>((props, ref) => {
  const { onChange, onBlur, thousandSeparator = false, value, ...other } = props;

  // 表示用のフォーマット済み値
  const formattedValue = React.useMemo(() => {
    if (value === undefined || value === '') return '';
    
    // 数値に変換できる場合はフォーマット
    const numValue = typeof value === 'number' ? value : Number(value);
    if (isNaN(numValue)) return value;
    
    // カンマ区切りが有効ならフォーマット、そうでなければそのまま
    return thousandSeparator ? numValue.toLocaleString() : String(value);
  }, [value, thousandSeparator]);

  // 全角数字を半角数字に変換する関数
  const convertFullToHalf = (value: string): string => {
    return value.replace(/[０-９]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
    });
  };

  // 数値のみを許可する関数（小数点とマイナス記号も許可）
  const validateNumericInput = (value: string): string => {
    // 数字、小数点、マイナス記号、カンマのみを許可
    return value.replace(/[^0-9.\-,]/g, '');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onChange) return;
    
    // 入力値からカンマを除去
    let inputValue = e.target.value;
    
    // カンマを除去（表示用フォーマットの影響を排除）
    inputValue = inputValue.replace(/,/g, '');
    
    // 全角数字を半角に変換し、数値以外の文字を除去
    let convertedValue = convertFullToHalf(inputValue);
    convertedValue = validateNumericInput(convertedValue);
    // 残っているカンマを除去（validateNumericInputでカンマを許可したため）
    convertedValue = convertedValue.replace(/,/g, '');

    const newEvent = {
      ...e,
      target: {
        ...e.target,
        value: convertedValue
      }
    };

    onChange(newEvent as React.ChangeEvent<HTMLInputElement>);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!onBlur) return;
    
    // カンマを除去
    let inputValue = e.target.value.replace(/,/g, '');
    
    // 全角数字を半角に変換し、数値以外の文字を除去
    let convertedValue = convertFullToHalf(inputValue);
    convertedValue = validateNumericInput(convertedValue);
    // 残っているカンマを除去
    convertedValue = convertedValue.replace(/,/g, '');

    // 空でなければ数値として整形
    if (convertedValue && !isNaN(Number(convertedValue))) {
      // 数値に変換して再フォーマット（小数点以下の0を取り除くなど）
      convertedValue = Number(convertedValue).toString();
    }

    const newEvent = {
      ...e,
      target: {
        ...e.target,
        value: convertedValue
      }
    };

    // 元のonBlurを呼び出し
    onBlur(newEvent as React.FocusEvent<HTMLInputElement>);

    // 値が変更された場合はonChangeも呼び出す
    if (convertedValue !== inputValue && onChange) {
      onChange(newEvent as any);
    }
  };

  return (
    <TextField
      {...other}
      value={formattedValue}
      ref={ref}
      onChange={handleChange}
      onBlur={handleBlur}
      inputProps={{
        ...props.inputProps,
        inputMode: 'decimal',
      }}
    />
  );
});

NumberTextField.displayName = 'NumberTextField';

export default NumberTextField;